import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationError, Router } from "@angular/router";
import { Store } from '@ngrx/store';
import { OidcSecurityService } from "angular-auth-oidc-client";
import { LoadingScreenComponent } from '../../shared/loading-screen/loading-screen.component';
import { InstallationSelectors } from '../../state/installations-state/installation-state.selectors';
import { installationPermissionsIsLoadingSelector } from '../../state/security-state/security-state.selectors';

@Component({
    selector: 'app-signin-oidc',
    imports: [LoadingScreenComponent],
    templateUrl: './signin-oidc.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './signin-oidc.component.scss'
})
export class SigninOidcComponent implements OnInit {

  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly store = inject(Store);

  private readonly isAuthenticated = signal(false);
  private readonly latestInstallations = this.store.selectSignal(InstallationSelectors.getLatestInstallations);
  private readonly latestInstallationsFailed = this.store.selectSignal(InstallationSelectors.getLatestInstallationsFailed);
  private readonly activeInstallation = this.store.selectSignal(InstallationSelectors.selectActiveInstallationDetails);
  private readonly permissionsLoading = this.store.selectSignal(installationPermissionsIsLoadingSelector);
  private readonly startupLoadFailed = this.store.selectSignal(InstallationSelectors.selectStartupLoadFailed);
  /** The navigation to '/' errored: the top groups a failed latest list falls back to failed too (PermissionGuard). */
  private readonly navigationErrored = signal(false);
  protected readonly failed = computed(() => this.startupLoadFailed() || this.navigationErrored());
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Which step of the login we are stuck on.
   *
   * This component stays mounted long after it has asked for '/': the router keeps the current view
   * on screen until the target route's guards resolve, and those guards wait on the latest
   * installations, then the active installation's details, then its permissions - each one a
   * separate round trip. Naming the step turns a blank wait into something the user can read.
   */
  protected readonly statusKey = computed(() => {
    if (!this.isAuthenticated()) return 'LOADING_SCREEN.SIGNING_IN';
    // A failed list is not waited on: the first top group opens instead (PermissionGuard).
    if (this.latestInstallations() === undefined && !this.latestInstallationsFailed()) return 'LOADING_SCREEN.INSTALLATIONS';
    if (!this.activeInstallation()) return 'LOADING_SCREEN.INSTALLATION';
    if (this.permissionsLoading()) return 'LOADING_SCREEN.PERMISSIONS';
    return 'LOADING_SCREEN.ALMOST_READY';
  });

  constructor(private _router: Router) {
  }

  ngOnInit() {
    this._router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationError) this.navigationErrored.set(true);
    });

    this.oidcSecurityService.isAuthenticated$.subscribe(({ isAuthenticated }) => {
      this.isAuthenticated.set(isAuthenticated);
      if (isAuthenticated) {
        this._router.navigateByUrl('/');
      } else {
        this._router.navigateByUrl('no-auth');
      }
    });
  }
}
